import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory, Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableHead, TableHeader, TableRow, TableCell } from '../ui/table';
import { Spinner } from '../ui/spinner';
import { Actions as DASHBOARDACTIONS } from '../../store/actions/dashboardActions';
import { NoPermissions } from '../Common/ReusableComponents/Reusable';

const formatTargetCoverage = (arr) => {
  if (!Array.isArray(arr)) return '';
  return arr
    .map(
      (item) =>
        item
          .replace(/(\d+)/, ' $1')
          .replace(/^./, (c) => c.toUpperCase()),
    )
    .join(', ');
};

const NetZeroRow = ({ target }) => (
  <TableRow className='hover:bg-muted/50 text-base'>
    <TableCell id='target-name' className='font-medium text-left'>
      {formatTargetCoverage(target?.targetCoverage)}
    </TableCell>
    <TableCell>{target?.settingMethod || 'Absolute Contraction'}</TableCell>
    <TableCell>{target?.type}</TableCell>
    <TableCell>{target?.baseYear}</TableCell>
    <TableCell>{target?.targetYear}</TableCell>
    <TableCell className='text-center'>
      <Button className='h-8 p-2 bg-secondary text-secondary-foreground hover:text-primary-foreground' asChild>
        <Link to={`/netZero/${target.id}`}>View/Edit</Link>
      </Button>
    </TableCell>
  </TableRow>
);

const NetZeroTable = ({ list, showLoader, getTargets, currentOrganisation, netZero }) => {
  const history = useHistory();

  useEffect(() => {
    if (!list || list?.length < 1) {
      getTargets();
    }
  }, [getTargets, currentOrganisation]);

  if (!netZero) {
    return <NoPermissions />;
  }

  const handleAddNetZero = () => history.push('/netZero/new');

  return (
    <div className='w-full font-body'>
      <Card className='border rounded-md'>
        <CardHeader className='flex flex-row items-center justify-between pb-2'>
          <CardTitle className='uppercase text-xl font-semibold font-body'>Science Based Targets</CardTitle>
          {!showLoader && (
            <div className='flex flex-row gap-3'>
              <Button
                id='target-add'
                className='font-semibold text-base bg-secondary text-secondary-foreground hover:text-primary-foreground'
                onClick={handleAddNetZero}>
                Add Net-Zero Target
              </Button>
              <Button
                id='target-add-near-term'
                disabled
                variant='secondary'
                className='font-semibold text-base opacity-70'>
                Add Near-Term Target (Coming Soon)
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className='p-0'>
          {showLoader && !list?.length ? (
            <div className='mt-4 w-full flex items-center justify-center p-8'>
              <Spinner size='large' />
            </div>
          ) : (
            <div className='overflow-x-auto m-4'>
              <Table className='w-full' id='science-based-targets-table'>
                <TableHeader>
                  <TableRow>
                    <TableHead>Target Coverage</TableHead>
                    <TableHead>Target Method</TableHead>
                    <TableHead>Target Type</TableHead>
                    <TableHead>Base Year</TableHead>
                    <TableHead>Target Year</TableHead>
                    <TableHead className='text-center'>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {list && list.length > 0 && list.map((target, index) => <NetZeroRow key={target.id || index} target={target} />)}
                  {(!list || list.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={6} className='text-center font-semibold text-base'>
                        No targets yet. Add a Net-Zero target to get started.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => {
  const { ambassadorAdmin, cyfAdmin, whiteLabelAdmin, consultantAdmin } = state?.profile?.details || {};
  const orgDetails = state?.organisation?.details;
  const currentOrganisation = state?.currentOrganisation;
  const adminUser = cyfAdmin || whiteLabelAdmin || consultantAdmin;
  const features = state?.features;
  const availableFeatures = state.organisation?.details?.features;
  const sbtFeature = features?.find((feature) => feature.featureKey === 'SCIENCE_BASED_TARGETS');
  const netZero = availableFeatures?.includes('SCIENCE_BASED_TARGETS') || sbtFeature?.enabledByDefault || false;

  return {
    list: state?.dashboard?.targets,
    branding: state.branding,
    showLoader: state?.showLoader,
    currentOrgDetails: orgDetails,
    currentOrganisation,
    adminUser,
    netZero,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getTargets: () => dispatch(DASHBOARDACTIONS.getTargets()),
});

export default connect(mapStateToProps, mapDispatchToProps)(NetZeroTable);
